import { useMemo } from "react";
import { GoogleMap, MarkerF, useJsApiLoader } from "@react-google-maps/api";
import { GOOGLE_MAPS_API_KEY, GOOGLE_MAPS_LIBRARIES, isMapsEnabled } from "@/lib/maps";
import { MapPin } from "lucide-react";

const DARK_STYLE = [
  { elementType: "geometry", stylers: [{ color: "#1f2937" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#cbd5e1" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#0f172a" }] },
  { featureType: "administrative", elementType: "geometry.stroke", stylers: [{ color: "#475569" }] },
  { featureType: "poi", elementType: "labels.text.fill", stylers: [{ color: "#94a3b8" }] },
  { featureType: "poi.park", elementType: "geometry", stylers: [{ color: "#064e3b" }] },
  { featureType: "road", elementType: "geometry", stylers: [{ color: "#334155" }] },
  { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#475569" }] },
  { featureType: "road", elementType: "labels.text.fill", stylers: [{ color: "#cbd5e1" }] },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#0c4a6e" }] },
];

/**
 * Reusable Google Map view.
 * Props:
 *   center: { lat, lng }
 *   zoom: number (default 13)
 *   markers: [{ lat, lng, title, id }]
 *   height: tailwind class or px (default "h-72")
 *   onMarkerClick: optional fn(marker)
 */
export default function GoogleMapView({ center, zoom = 13, markers = [], height = "h-72", onMarkerClick }) {
  const { isLoaded, loadError } = useJsApiLoader({
    id: "gmaps-script",
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: GOOGLE_MAPS_LIBRARIES,
  });

  const opts = useMemo(() => ({
    styles: DARK_STYLE,
    disableDefaultUI: false,
    streetViewControl: false,
    mapTypeControl: false,
    fullscreenControl: false,
    backgroundColor: "#0f172a",
  }), []);

  if (!isMapsEnabled()) {
    return (
      <div className={`${height} w-full bg-slate-900 rounded-xl flex items-center justify-center text-slate-500 text-sm`}>
        <MapPin className="ml-2" size={16} /> Maps not configured
      </div>
    );
  }
  if (loadError) {
    return <div className={`${height} w-full bg-slate-900 rounded-xl flex items-center justify-center text-red-400 text-sm`}>Map failed to load</div>;
  }
  if (!isLoaded) {
    return <div className={`${height} w-full bg-slate-900 rounded-xl animate-pulse`} />;
  }

  return (
    <div className={`${height} w-full rounded-xl overflow-hidden`} data-testid="google-map">
      <GoogleMap mapContainerStyle={{ width: "100%", height: "100%" }} center={center} zoom={zoom} options={opts}>
        {markers.map((m, i) => (
          <MarkerF
            key={m.id || i}
            position={{ lat: m.lat, lng: m.lng }}
            title={m.title}
            onClick={onMarkerClick ? () => onMarkerClick(m) : undefined}
          />
        ))}
      </GoogleMap>
    </div>
  );
}
